import { FRAME_MS_MAX, FRAME_MS_MIN, REST_MS_MAX, STRIP_FRAME_MS, animationTiming, sameTiming, type StripTiming } from "../assets/timing.ts";
import type { StripPlays } from "./strips.ts";

/**
 * The timing panel's draft (ADR-107 decisions 20 to 22 and its note on P1.14-T11): a strip's timing as the panel
 * holds it while it is edited. Every value is spelt out, one time for each frame included, so the preview plays the
 * draft as it stands; a save turns it back into the stored shape, which says only what differs from the defaults.
 */

/**
 * A strip's timing while it is edited: each frame's milliseconds, whether the frames are timed one by one ("Per
 * frame") or share one value, the rest, the index of the frame the rest falls on, and how the strip plays.
 */
export interface TimingDraft {
  readonly frameMs: readonly number[];
  readonly perFrame: boolean;
  readonly restMs: number;
  readonly restOn: number;
  readonly plays: StripPlays;
}

/**
 * The draft the panel opens on: what the stored timing says, and decision 21's defaults for what it does not (100 ms
 * a frame, no rest, the rest on the first frame, the strip's own plays). A stored list that does not hold one time
 * for each frame, or a rest on a frame past the strip, gives the default for that part.
 *
 * @param frames The strip's frame count.
 * @param plays How the strip plays when its timing does not say.
 * @param stored The stored timing, or null for none.
 * @returns The draft.
 */
export function draftOfTiming(frames: number, plays: StripPlays, stored: StripTiming | null): TimingDraft {
  const given = stored?.frameMs;
  const listed = typeof given === "object" && given.length === frames;
  const frameMs = listed ? [...given] : Array.from({ length: frames }, () => (typeof given === "number" ? given : STRIP_FRAME_MS));
  const restOn = stored?.restOn ?? 0;
  return { frameMs, perFrame: listed, restMs: stored?.restMs ?? 0, restOn: restOn >= 0 && restOn < frames ? restOn : 0, plays: stored?.plays ?? plays };
}

/**
 * Gives every frame one time.
 *
 * @param draft The draft.
 * @param ms The time in milliseconds.
 * @returns The draft, its frames no longer timed one by one.
 */
export function withEveryFrameMs(draft: TimingDraft, ms: number): TimingDraft {
  return { ...draft, frameMs: draft.frameMs.map(() => ms), perFrame: false };
}

/**
 * Gives one frame its own time.
 *
 * @param draft The draft.
 * @param index The frame's index.
 * @param ms The time in milliseconds.
 * @returns The draft; the same one when the strip has no such frame.
 */
export function withFrameMs(draft: TimingDraft, index: number, ms: number): TimingDraft {
  return index < 0 || index >= draft.frameMs.length ? draft : { ...draft, frameMs: draft.frameMs.map((was, at) => (at === index ? ms : was)) };
}

/**
 * Turns "Per frame" on or off. On, the times stay as they are and each may then differ; off, every frame takes the
 * first frame's time.
 *
 * @param draft The draft.
 * @param perFrame Whether the frames are timed one by one.
 * @returns The draft.
 */
export function withPerFrame(draft: TimingDraft, perFrame: boolean): TimingDraft {
  return perFrame ? { ...draft, perFrame: true } : withEveryFrameMs(draft, draft.frameMs[0] ?? STRIP_FRAME_MS);
}

/**
 * The durations the preview plays for a draft: each frame's time, the rest added to its frame.
 *
 * @param draft The draft.
 * @returns One duration for each frame.
 * @throws RangeError when the rest falls on a frame past the strip.
 */
export function draftDurations(draft: TimingDraft): number[] {
  return animationTiming(draft.frameMs, draft.restMs, draft.restOn);
}

/**
 * The timing a save sends for a draft: one frame time or the list, the rest and its frame only when there is a
 * rest (the first frame left unsaid), and how it plays only when that is not the strip's own or the stored timing
 * already said it.
 *
 * @param draft The draft.
 * @param plays How the strip plays when its timing does not say.
 * @param stored The stored timing, or null for none.
 * @returns The timing.
 */
export function timingOfDraft(draft: TimingDraft, plays: StripPlays, stored: StripTiming | null): StripTiming {
  return {
    frameMs: draft.perFrame ? [...draft.frameMs] : (draft.frameMs[0] ?? STRIP_FRAME_MS),
    ...(draft.restMs > 0 ? { restMs: draft.restMs, ...(draft.restOn > 0 ? { restOn: draft.restOn } : {}) } : {}),
    ...(draft.plays !== plays || stored?.plays !== undefined ? { plays: draft.plays } : {}),
  };
}

/**
 * Whether a draft differs from what is stored: what it would send is not what the stored timing, or with none the
 * default, would send.
 *
 * @param draft The draft.
 * @param plays How the strip plays when its timing does not say.
 * @param stored The stored timing, or null for none.
 * @returns True when a save would change the strip's timing.
 */
export function draftChanged(draft: TimingDraft, plays: StripPlays, stored: StripTiming | null): boolean {
  return !sameTiming(timingOfDraft(draft, plays, stored), timingOfDraft(draftOfTiming(draft.frameMs.length, plays, stored), plays, stored));
}

/**
 * Why a draft cannot be saved, within the bounds every reader of a timing holds it to.
 *
 * @param draft The draft.
 * @returns The sentence, or null when it may be saved.
 */
export function draftRefusal(draft: TimingDraft): string | null {
  const bad = draft.frameMs.findIndex((ms) => !Number.isInteger(ms) || ms < FRAME_MS_MIN || ms > FRAME_MS_MAX);
  if (bad >= 0) {
    return `${draft.perFrame ? `Frame ${bad + 1}'s` : "A frame's"} time is a whole number from ${FRAME_MS_MIN} to ${FRAME_MS_MAX.toLocaleString("en-GB")} ms`;
  }
  if (!Number.isInteger(draft.restMs) || draft.restMs < 0 || draft.restMs > REST_MS_MAX) {
    return `The rest is a whole number from 0 to ${REST_MS_MAX.toLocaleString("en-GB")} ms`;
  }
  if (!Number.isInteger(draft.restOn) || draft.restOn < 0 || draft.restOn >= draft.frameMs.length) {
    return `The rest falls on a frame of the strip, 1 to ${draft.frameMs.length}`;
  }
  return null;
}
